import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <Link href="/" className="inline-flex items-center gap-2 text-primary hover:underline mb-8" data-testid="link-home">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-primary mb-8" data-testid="text-privacy-title">
          Privacy Policy
        </h1>

        <div className="bg-background/80 backdrop-blur-sm rounded-lg p-6 md:p-8 space-y-8">
          <p className="text-muted-foreground leading-relaxed" data-testid="text-privacy-intro">
            EduFocal has established this privacy policy to explain to you how your information is protected, collected and used. We may update this policy from time to time. If EduFocal makes materially significant changes to this privacy policy, we will provide notice thereof by posting notice on EduFocal site; otherwise, you should review this privacy policy on a regular basis to keep yourself apprised of any less significant changes.
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-data-collection">
              1. Data we collect and store
            </h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3" data-testid="text-data-point-1">
                <span className="text-primary mt-1">•</span>
                <span>For registered users, we collect personal and contact information in connection with the creation of your EduFocal account. You can access and update your account information through the account homepage. This information is stored on EduFocal servers indefinitely, even if you terminate your account.</span>
              </li>
              <li className="flex gap-3" data-testid="text-data-point-2">
                <span className="text-primary mt-1">•</span>
                <span>We collect content that users post on the EduFocal site, such as sample answers to questions, comments, and biographical information (for teachers). This content is stored in our database, even after "deletion". Also, search engines and other sites not affiliated with EduFocal - including archive.org, google.com, and groupsyahoo.com - may archive or otherwise make available content contained in EduFocal postings.</span>
              </li>
              <li className="flex gap-3" data-testid="text-data-point-3">
                <span className="text-primary mt-1">•</span>
                <span>We collect biographical and other personal information about registered users that you post in certain optional areas of the EduFocal site. Please do not post personal information on public areas of the EduFocal site (such as answers to questions, comments, messages to teachers or other users) that you expect to keep private.</span>
              </li>
              <li className="flex gap-3" data-testid="text-data-point-4">
                <span className="text-primary mt-1">•</span>
                <span>We collect your email address for purposes such as: user registration, sending confirmation emails, authenticating user accounts, providing subscription email services, etc.</span>
              </li>
              <li className="flex gap-3" data-testid="text-data-point-5">
                <span className="text-primary mt-1">•</span>
                <span>Our web logs collect standard web log entries for each page served, including your IP address, page URL, and timestamp. Web logs help us to diagnose problems with our server, to administer the EduFocal site, and to otherwise provide our service to you. These web logs and other records are stored indefinitely.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-protecting-privacy">
              2. Protecting your privacy
            </h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3" data-testid="text-privacy-point-1">
                <span className="text-primary mt-1">•</span>
                <span>Although we make good faith efforts to store the information we store in a secure operating environment that is not available to the public, we cannot guarantee complete security.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-2">
                <span className="text-primary mt-1">•</span>
                <span>For registered users, we may enable other registered users to contact you via the EduFocal messaging functionality. However, your contact information will not be available to other registered users or the public areas of the EduFocal site, in which case other registered users, and potentially other individuals, may be able to access it.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-3">
                <span className="text-primary mt-1">•</span>
                <span>Your name and certain biographical information that you choose to provide to us, including your grade, your school, and a photo (if you upload one), may be available to other registered users in certain areas of the EduFocal site, including via your Profile page, the Leaderboard, and the Question Queue.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-4">
                <span className="text-primary mt-1">•</span>
                <span>We don't share your information with third parties for marketing purposes.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-5">
                <span className="text-primary mt-1">•</span>
                <span>We don't engage in cross-marketing or link-referral programs with other sites.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-6">
                <span className="text-primary mt-1">•</span>
                <span>We don't employ tracking devices such as cookies for marketing purposes.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-7">
                <span className="text-primary mt-1">•</span>
                <span>We don't send you unsolicited communications for marketing purposes.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-8">
                <span className="text-primary mt-1">•</span>
                <span>Account information is password-protected. Keep your password confidential and do not share it with others.</span>
              </li>
              <li className="flex gap-3" data-testid="text-privacy-point-9">
                <span className="text-primary mt-1">•</span>
                <span>EduFocal, or people who post on EduFocal, may provide links to third party websites, which may have different privacy practices. We are not responsible for, nor have any control over, the privacy policies of those third party websites, and encourage all users to read the privacy policies of each and every website visited.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4" data-testid="text-section-release-info">
              3. Circumstances in which EduFocal may release information
            </h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3" data-testid="text-release-point-1">
                <span className="text-primary mt-1">•</span>
                <span>We may disclose information about our registered users if required to do so by law or in the good faith belief that such disclosure is reasonably necessary to respond to subpoenas, court orders, or other legal process.</span>
              </li>
              <li className="flex gap-3" data-testid="text-release-point-2">
                <span className="text-primary mt-1">•</span>
                <span>We may also disclose information about our registered users to law enforcement officers or others, in the good faith belief that such disclosure is reasonably necessary to enforce our Terms of Use, respond to claims that any posting or other content violates the rights of third-parties, or protect the rights, property, or personal safety of EduFocal, its users or the general public.</span>
              </li>
            </ul>
          </section>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground" data-testid="text-last-updated">
              Last updated: October 1, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
